import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import socket from '../utils/socket';
import { getStatus, createExperiment, startExperiment } from '../utils/api';
import StatusBadge from '../components/StatusBadge';
import SensorCard  from '../components/SensorCard';

const MQ_SENSORS = [
  { key:'mq2',  label:'MQ-2  (LPG/Smoke)',     color:'#f87171' },
  { key:'mq3',  label:'MQ-3  (Alcohol)',        color:'#fb923c' },
  { key:'mq4',  label:'MQ-4  (Methane)',        color:'#facc15' },
  { key:'mq5',  label:'MQ-5  (LPG/Natural)',    color:'#a3e635' },
  { key:'mq6',  label:'MQ-6  (LPG)',            color:'#34d399' },
  { key:'mq7',  label:'MQ-7  (CO)',             color:'#22d3ee' },
  { key:'mq8',  label:'MQ-8  (H2)',             color:'#818cf8' },
  { key:'mq9',  label:'MQ-9  (CO/Flammable)',   color:'#c084fc' },
  { key:'mq135',label:'MQ-135 (Air Quality)',   color:'#f472b6' },
];
const ENV_SENSORS = [
  { key:'ens160_eco2', label:'ENS160 eCO2',   unit:'ppm',  color:'#67e8f9' },
  { key:'ens160_tvoc', label:'ENS160 TVOC',   unit:'ppb',  color:'#a5b4fc' },
  { key:'ens160_aqi',  label:'ENS160 AQI',    unit:'',     color:'#86efac' },
  { key:'aht21_temp',  label:'AHT21 Temp',    unit:'°C',   color:'#fca5a5' },
  { key:'aht21_hum',   label:'AHT21 Humidity',unit:'%',    color:'#93c5fd' },
  { key:'sgp30_co2',   label:'SGP30 CO2',     unit:'ppm',  color:'#fde68a' },
  { key:'sgp30_tvoc',  label:'SGP30 TVOC',    unit:'ppb',  color:'#d9f99d' },
];

const grid = { display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(160px,1fr))', gap:12 };
const inputStyle = {
  background:'#1e293b', border:'1px solid #334155', color:'#e2e8f0',
  borderRadius:8, padding:'8px 12px', width:'100%', fontSize:14,
};
const label = { fontSize:13, color:'#94a3b8', marginBottom:4, display:'block' };
const btn   = (disabled, color='#0ea5e9') => ({
  background: disabled ? '#334155' : color,
  color: disabled ? '#64748b' : '#fff',
  border:'none', borderRadius:8, padding:'10px 22px',
  fontSize:15, fontWeight:600, cursor: disabled ? 'not-allowed' : 'pointer',
  transition:'background 0.2s',
});

export default function Lobby() {
  const navigate = useNavigate();
  const [status,   setStatus]   = useState(null);
  const [online,   setOnline]   = useState(false);
  const [sensors,  setSensors]  = useState({});
  const [form,     setForm]     = useState({ title:'', duration:3600, log_interval:10 });
  const [loading,  setLoading]  = useState(false);
  const statusTimer = useRef(null);

  // Poll status every 30s
  const fetchStatus = async () => {
    try {
      const { data } = await getStatus();
      setStatus(data.status);
      setOnline(data.online);
    } catch {}
  };

  useEffect(() => {
    fetchStatus();
    statusTimer.current = setInterval(fetchStatus, 30_000);
    return () => clearInterval(statusTimer.current);
  }, []);

  // Live sensors via Socket.IO
  useEffect(() => {
    const onLive   = (d) => setSensors(d);
    const onStatus = (d) => { setStatus(d.status); setOnline(true); };
    socket.on('sensors:live',  onLive);
    socket.on('status:update', onStatus);
    return () => { socket.off('sensors:live', onLive); socket.off('status:update', onStatus); };
  }, []);

  const handleStart = async () => {
    if (loading) return;
    setLoading(true);
    try {
      // 1. Create experiment record in DB → get id
      const { data: { id } } = await createExperiment({
        title:        form.title,
        duration:     Number(form.duration),
        log_interval: Number(form.log_interval),
      });
      // 2. Send start command via REST → backend publishes MQTT
      await startExperiment({
        experiment_id: id,
        duration:      Number(form.duration),
        log_interval:  Number(form.log_interval),
      });
      navigate(`/experiment/${id}/running`);
    } catch (e) {
      alert('เกิดข้อผิดพลาด: ' + (e.response?.data?.error || e.message));
    } finally {
      setLoading(false);
    }
  };

  const ready   = status === 'Ready' && online;
  const canStart = ready && form.title.trim() && form.duration > 0 && form.log_interval > 0;

  return (
    <div style={{ maxWidth:1100, margin:'0 auto' }}>
      <div style={{ display:'flex', alignItems:'center', gap:16, marginBottom:24 }}>
        <h1 style={{ fontSize:24, fontWeight:700 }}>Lobby</h1>
        <StatusBadge status={online ? status : 'Offline'} />
        {!online && <span style={{ color:'#ef4444', fontSize:13 }}>ESP32 ออฟไลน์ หรือไม่ได้รับสถานะภายใน 40 นาที</span>}
      </div>

      {/* Live Sensor Preview */}
      <section style={{ marginBottom:28 }}>
        <h2 style={{ fontSize:16, color:'#94a3b8', marginBottom:12 }}>
          ค่า Sensor แบบ Real-time (Live Preview)
          <span style={{ fontSize:12, marginLeft:10 }}>อัปเดตทุก 5 วินาที — ไม่บันทึก DB</span>
        </h2>
        <div style={{ marginBottom:12 }}>
          <p style={{ fontSize:13, color:'#64748b', marginBottom:8 }}>MQ Sensors (Voltage)</p>
          <div style={grid}>
            {MQ_SENSORS.map(s => (
              <SensorCard key={s.key} label={s.label} value={sensors[s.key]} unit="V" color={s.color} />
            ))}
          </div>
        </div>
        <div>
          <p style={{ fontSize:13, color:'#64748b', marginBottom:8 }}>Environmental Sensors</p>
          <div style={grid}>
            {ENV_SENSORS.map(s => (
              <SensorCard key={s.key} label={s.label} value={sensors[s.key]} unit={s.unit} color={s.color} />
            ))}
          </div>
        </div>
      </section>

      {/* Experiment Form */}
      <section style={{ background:'#1e293b', borderRadius:12, padding:24 }}>
        <h2 style={{ fontSize:18, fontWeight:600, marginBottom:16 }}>เริ่มการทดลอง</h2>
        {!online && (
          <p style={{ color:'#ef4444', marginBottom:16 }}>
            รอ ESP32 เชื่อมต่อและวอร์มอัพ MQ Sensor ให้เสร็จก่อน (30 นาที) จึงจะสามารถเริ่มการทดลองได้
          </p>
        )}
        {online && status === 'Preheat' && (
          <p style={{ color:'#f59e0b', marginBottom:16 }}>ESP32 กำลังวอร์มอัพ MQ Sensor (30 นาที) — กรุณารอ</p>
        )}

        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:16, marginBottom:20 }}>
          <div>
            <label style={label}>ชื่อการทดลอง (Experiment Title)</label>
            <input
              style={inputStyle} disabled={!ready}
              value={form.title}
              onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
              placeholder="เช่น ทดสอบก๊าซ LPG"
            />
          </div>
          <div>
            <label style={label}>ระยะเวลาการทดลอง (วินาที)</label>
            <input
              type="number" style={inputStyle} disabled={!ready} min={10}
              value={form.duration}
              onChange={e => setForm(f => ({ ...f, duration: e.target.value }))}
            />
          </div>
          <div>
            <label style={label}>ระยะห่างในการบันทึก (วินาที)</label>
            <input
              type="number" style={inputStyle} disabled={!ready} min={1}
              value={form.log_interval}
              onChange={e => setForm(f => ({ ...f, log_interval: e.target.value }))}
            />
          </div>
        </div>

        <button style={btn(!canStart || loading)} disabled={!canStart || loading} onClick={handleStart}>
          {loading ? 'กำลังเริ่ม...' : 'Start Experiment'}
        </button>
      </section>
    </div>
  );
}
